import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { TEAMS_QUERY_KEY } from '../constants';
import { deleteUser } from '@modules/teams/services';
import { DeleteUserContext, DeleteUserVariables, TeamListItem } from '../types';

function useMutationDeleteUser(
  onSuccess: (data: any, variables: DeleteUserVariables, context: DeleteUserContext | undefined) => void,
  onError?: () => void,
): UseMutationResult<string, unknown, DeleteUserVariables, DeleteUserContext> {
  const queryClient = useQueryClient();

  const mutationFn = async (id: any): Promise<any> => {
    await deleteUser(id);
  };

  const onMutateFn = async (id: DeleteUserVariables): Promise<DeleteUserContext> => {
    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({ queryKey: [TEAMS_QUERY_KEY] });

    // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries([TEAMS_QUERY_KEY]);

    const previousUsers = queryClient.getQueryData([TEAMS_QUERY_KEY]) as TeamListItem[];

    // Optimistically update to the new value
    queryClient.setQueryData([TEAMS_QUERY_KEY], (old: TeamListItem[] | undefined): TeamListItem[] | undefined =>
      old?.filter((t) => t.id !== id),
    );

    // Return a context object with the snapshotted value
    return { previousUsers };
  };

  const onErrorFn = (_err: unknown, _id: string, context: DeleteUserContext | undefined): void => {
    if (context === undefined) {
      return;
    }
    if (context.previousUsers !== undefined) {
      queryClient.setQueryData([TEAMS_QUERY_KEY], context.previousUsers);
    }
    onError?.();
  };

  const onSettledFn = async (): Promise<void> => {
    await queryClient.invalidateQueries([TEAMS_QUERY_KEY]);
  };

  return useMutation<string, unknown, DeleteUserVariables, DeleteUserContext>(['deleteUser'], mutationFn, {
    onMutate: onMutateFn,
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: onErrorFn,
    // Always refetch after error or success:
    onSettled: onSettledFn,
    onSuccess,
  });
}

export default useMutationDeleteUser;
